import React, { useCallback, useMemo, useRef, useState } from 'react';
import GridItem from '../../../../layout/GridComponents/GridItem';
import {
  NavReconOverviewObject,
  NavReconPipelineMetadata,
} from '../../../../../api/rs-hg/nav-recon/types';
import { sortFundNames } from '../../../../../utilities/customSorting';
import {
  NavReconOverviewExpandedTableRow,
  getNavReconOverviewExpandedColumns,
} from '../navReconOverviewExpandedColumns';
import NavReconOverviewDetailPanel from './NavReconOverviewDetailPanel';
import {
  buildPipelineProgressByFund,
  getNavReconFundPipelineProgressForFundName,
} from '../pipelineMetadata';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  ExpandedState,
  SortingState,
} from '@tanstack/react-table';
import SearchBar from '../../../../ui/inputs/SearchBar';
import ExportButton from '../../../../feedback/ExportButton';
import { HiChevronUp, HiChevronDown, HiOutlineChevronUpDown } from 'react-icons/hi2';
import NavReconOverviewStats from './NavReconOverviewStats';
import {
  columnWidthStyle,
  getColumnAlignMeta,
} from '../../../../tables/tanstack/tanstackUtils';
import { useDemoFundIds } from '../../../../../hooks/useDemoFundIds';

interface Props {
  data: NavReconOverviewObject[];
  pipelineMetadata: NavReconPipelineMetadata[];
  positionDate: string;
  section: string;
  pageId?: string;
}

/**
 * Full `NavReconOverviewObject[]` grid for the NAV-Recon overview (expanded)
 * route (`get_nav_recon_overview`).
 * Refactored to use @tanstack/react-table directly like the raidr v2 overview page.
 */
const NavReconOverviewExpandedTable: React.FC<Props> = ({
  data,
  pipelineMetadata,
  positionDate,
  section,
  pageId,
}) => {
  const demoFundIds = useDemoFundIds(section, pageId);
  const [globalFilter, setGlobalFilter] = useState('');
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const sortingRef = useRef(sorting);
  sortingRef.current = sorting;
  const getSorting = useCallback(() => sortingRef.current, []);

  const pipelineProgressByFund = useMemo(
    () => buildPipelineProgressByFund(pipelineMetadata),
    [pipelineMetadata],
  );

  const rows = useMemo<NavReconOverviewExpandedTableRow[]>(
    () =>
      [...data]
        .sort((a, b) => sortFundNames(a.fund_name || '', b.fund_name || ''))
        .map((item, index) => {
          const d = item.data ?? {};
          return {
            ...d,
            fund_name: item.fund_name,
            fund_id: item.fund_id,
            fund_name_full: item.fund_name_full ?? '',
            subfund_code: item.subfund_code,
            index,
            overviewHasPayload:
              item.data != null && Object.keys(item.data).length > 0,
            pipelineProgress: getNavReconFundPipelineProgressForFundName(
              item.fund_name,
              pipelineProgressByFund,
            ),
            // Flatten nested values onto the row so CSV export picks them up.
            assetClass_Cash: d.asset_class_returns?.Cash,
            assetClass_Equity: d.asset_class_returns?.Equity,
            assetClass_Commodity: d.asset_class_returns?.Commodity,
            assetClass_Fixed_Income: d.asset_class_returns?.['Fixed Income'],
            assetClass_Forex: d.asset_class_returns?.Forex,
            assetClass_Alts: d.asset_class_returns?.Alts,
            alertStatus_six_caceis_returns_status:
              d.alert_statuses?.six_caceis_returns_status,
            alertStatus_caceis_share_price_returns_status:
              d.alert_statuses?.caceis_share_price_returns_status,
            alertStatus_six_share_price_returns_status:
              d.alert_statuses?.six_share_price_returns_status,
            alertStatus_sc_nav_px_returns_status:
              d.alert_statuses?.sc_nav_px_returns_status,
            alertStatus_sc_unit_status: d.alert_statuses?.sc_unit_status,
            isDemoFund: demoFundIds.has(item.fund_name),
          };
        }),
    [data, demoFundIds, pipelineProgressByFund],
  );

  const columns = useMemo(
    () => getNavReconOverviewExpandedColumns(getSorting),
    [getSorting],
  );

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      globalFilter,
      expanded,
      sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    onSortingChange: setSorting,
  } as any);

  const { csvFields, fieldsMap } = useMemo(() => {
    const exportable = columns.filter(
      (col: any) => col.accessorKey && col.accessorKey !== 'index' && col.accessorKey !== 'expand'
    );
    return {
      csvFields: exportable.map((col: any) => col.accessorKey as string),
      fieldsMap: exportable.map((col: any) => ({
        key: col.accessorKey as string,
        label: col.header as string,
      })),
    };
  }, [columns]);

  return (
    <GridItem
      card
      xs={12}
      height="calc(100vh - 9rem)"
      cardStyle={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="border-b border-gray-200 px-4 py-3 flex-shrink-0">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex-1 w-full min-w-0">
            <NavReconOverviewStats
              title="NAV-Recon Overview (Expanded)"
              rows={rows}
              showKpis={false}
            />
          </div>
          <div className="flex items-center space-x-3 w-full md:w-auto justify-end flex-shrink-0">
            <SearchBar
              searchHandler={setGlobalFilter}
              placeholder="Search overview..."
              className="py-1 text-xs max-w-[180px]"
            />
            <ExportButton
              exportData={rows}
              pdfIdentifier="nav-recon-overview-expanded"
              fields={csvFields}
              fieldsMap={fieldsMap}
              selectedPositionDate={positionDate}
              allowPdfExport={true}
              linkToRaptorExport={true}
            />
          </div>
        </div>
      </div>

      <div className="overflow-auto flex-1 min-h-0 w-full relative">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10 bg-mainblue text-white shadow-sm">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const meta = getColumnAlignMeta(header.column.columnDef.meta);
                  const style = columnWidthStyle(meta);
                  const headerClass = (() => {
                    let classes =
                      'px-1.5 py-2 select-none cursor-pointer border-0 border-b border-blue-800 border-r-0 hover:bg-white/10 bg-mainblue ';
                    if (meta?.align === 'center') {
                      classes += 'text-center text-[10px] font-bold uppercase tracking-wider';
                    } else {
                      classes += 'text-left text-[10px] font-bold uppercase tracking-wider';
                    }
                    return classes;
                  })();

                  return (
                    <th
                      key={header.id}
                      style={style}
                      className={headerClass}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div
                        className="relative flex items-center w-full group/header"
                      >
                        <span className={`w-full font-semibold text-[10px] uppercase tracking-wider text-blue-100 group-hover/header:text-white transition-colors duration-150 pr-2.5 ${
                          meta?.align === 'center' ? 'text-center' : 'text-left'
                        }`}>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </span>
                        {header.column.getCanSort() && (
                          <span className="absolute right-0 top-1/2 -translate-y-1/2 transition-all duration-200 flex-shrink-0">
                            {header.column.getIsSorted() === 'asc' ? (
                              <HiChevronUp className="w-3.5 h-3.5 text-white" />
                            ) : header.column.getIsSorted() === 'desc' ? (
                              <HiChevronDown className="w-3.5 h-3.5 text-white" />
                            ) : (
                              <HiOutlineChevronUpDown className="w-3.5 h-3.5 text-blue-300/40 group-hover/header:text-blue-200" />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-12 text-center text-gray-500 text-sm"
                >
                  No funds found
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => {
                const rowData = row.original as NavReconOverviewExpandedTableRow;
                const isInactive =
                  !rowData.overviewHasPayload ||
                  rowData.pipelineProgress?.status === 'pending';
                const baseRowClass = isInactive
                  ? 'bg-gray-100 text-gray-500 border-b border-gray-200'
                  : rowData.isDemoFund
                    ? 'bg-demo_bg hover:bg-[#e9d5ff] border-b border-gray-200'
                    : 'bg-white hover:bg-gray-50 border-b border-gray-200';

                return (
                  <React.Fragment key={row.id}>
                    <tr className={baseRowClass}>
                      {row.getVisibleCells().map((cell) => {
                        const meta = getColumnAlignMeta(cell.column.columnDef.meta);
                        const style = columnWidthStyle(meta);
                        const cellClass = `px-1.5 py-1.5 text-xs border-b border-gray-100 border-r-0 ${
                          meta?.align === 'center' ? 'text-center' : 'text-left'
                        }`;

                        return (
                          <td key={cell.id} style={style} className={cellClass}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {row.getIsExpanded() && (
                      <tr>
                        <td colSpan={columns.length} className="bg-gray-50 p-0">
                          <NavReconOverviewDetailPanel row={rowData} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </GridItem>
  );
};

export default NavReconOverviewExpandedTable;
